import {
  ClassViewGridQuery,
  IngredientQuery,
} from "@/queries/autogenerate/graphql";
import { compareTwoStrings } from "string-similarity";
import { sortBy } from "./util";

// Types
type Unit = NonNullable<IngredientQuery["ingredient"]>["units"][number];
type Ingredient = Pick<
  NonNullable<IngredientQuery["ingredient"]>,
  "measurement" | "units" | "density"
>;
type Price = NonNullable<IngredientQuery["ingredient"]>["prices"][number];
type Order = Pick<
  ClassViewGridQuery["orderedIngredients"][number]["orders"][number],
  "amount" | "unit"
>;

/********************************************************************
 ***      functions for working with measurements and units       ***
 ********************************************************************/

export const MEASUREMENT_ID_VOLUME = "cjos1hvuf3bog0a16wrjjbshx";
export const MEASUREMENT_ID_WEIGHT = "cjos1kt053c310a16c05mt8zy";

export const BASE_UNIT = 1000;

export const unitToOption = (unit: Unit) => ({
  label: unit.name,
  value: unit.id,
});

export const getUnitOptions = (units: Unit[], density?: number) =>
  [...units]
    .reverse()
    .filter((unit) => (density ? true : !unit.isMass))
    .map(unitToOption);

export const getBaseUnit = (units: Unit[], defaultUnit?: Unit) =>
  units.find((unit) => unit.id === defaultUnit?.id) ||
  units.find((unit) => unit.name.includes("Grams")) ||
  units.find((unit) => unit.name.includes("Millilitres")) ||
  (units.length
    ? units.reduce((acc, cur) => {
        // Relative amount of the defending champion 💪🏾
        const accLogDistance = Math.abs(getBaseLog(BASE_UNIT, acc.amount));

        // Relative amount of the challenger
        const curLogDistance = Math.abs(getBaseLog(BASE_UNIT, cur.amount));

        // Return the lower logarithmic distance
        if (accLogDistance < curLogDistance) {
          return acc;
        } else {
          return cur;
        }
      })
    : null);

export const searchForUnit = (
  units: Unit[],
  search: string,
  density?: number,
  defaultUnit?: Unit
) => {
  const compare = (unit: Unit) => compareTwoStrings(unit.name, search);

  const matchingUnits = units
    .filter((unit) => (density ? true : !unit.isMass))
    .filter((unit) => compare(unit) >= 0.6)
    .sort((a, b) => compare(a) - compare(b));

  const [highest] = matchingUnits;

  return highest || getBaseUnit(units, defaultUnit);
};

// We often need to just look at all units relevant to an ingredient
// It is very repetivive to keep combining measurement units and discrete units
// Hence this helper function. Added bonus of being easily searchable via name
export const combineUnits = (ingredient: Ingredient) =>
  ingredient
    ? [
        ...(ingredient?.measurement?.units.filter((unit) =>
          ingredient.density ? true : !unit.isMass
        ) || []),
        ...ingredient.units,
      ]
    : [];

/*
 * Converting between weights and volumes
 *
 * BIG PICTURE
 **********************************************************
 *       ×n        (m^3) × p       ÷1000      ÷n          *
 *       <===        <===          <===      <===         *
 * Mass        kg             m^3         L        Volume *
 *       ===>        ===>          ===>      ===>         *
 *       ÷n          kg/p          ×1000      ×n          *
 **********************************************************
 *
 * LEGEND
 * Mass   => The specific unit we want to start or finish with.
 *           Examples include kg, g etc.
 * Volume => The specific unit we want to start or finish with.
 *           Examples include ml, L, Tbsp etc.
 * n      => multiplier. Takes us to or from our final or inital value.
 *           E.g. going from Cups to L or vice versa, n = 4
 *           E.g. going from g to kg or vice versa, n = 1000
 * p      => density of the ingredient
 *
 * what are units vs measurements?
 * What is a base unit?
 * what is an absolute amount?
 * what is a relative amount?
 */

// converting between weights and volumes
// where the unit of weight is kilogram and the unit of volume is metres cubed

// Litres to Volume multipliers
const LITRES_TO_CUPS = 4;
const LITRES_TO_TABLESPOONS = 50;
const LITRES_TO_TEASPOONS = 200;
const LITRES_TO_METRESCUBED = 1000;

// Kilos to Mass multipliers
const KILOS_TO_MILLIGRAMS = 1000000;
const KILOS_TO_GRAMS = 1000;

const massToKilograms = (mass: number, multiplier: number) => mass / multiplier;
const milligramsToKilograms = (milligrams: number) =>
  massToKilograms(milligrams, KILOS_TO_MILLIGRAMS);

const kilogramsToMass = (kg: number, n: number) => kg * n;
const kilogramsToMilligrams = (kg: number) =>
  kilogramsToMass(kg, KILOS_TO_MILLIGRAMS);
const kilogramsToGrams = (kg: number) => kilogramsToMass(kg, KILOS_TO_GRAMS);

const kilogramsToMetresCubed = (kg: number, p: number) => kg / p;
const metresCubedToKilograms = (metresCubed: number, p: number) =>
  metresCubed * p;

const metresCubedToLitres = (metresCubed: number) =>
  metresCubed * LITRES_TO_METRESCUBED;
const litresToMetresCubed = (litres: number) => litres / LITRES_TO_METRESCUBED;
const litresToVolume = (litres: number, n: number) => litres * n;
const volumeToLitres = (volume: number, n: number) => volume / n;

export const volumeToMilligrams = (
  volume: number,
  multiplier: number,
  density: number
) => {
  const litres = volumeToLitres(volume, multiplier);
  const metresCubed = litresToMetresCubed(litres);
  const kilograms = metresCubedToKilograms(metresCubed, density);
  const milligrams = kilogramsToMilligrams(kilograms);

  return milligrams;
};

export const milligramsToVolume = (
  milligrams: number,
  multiplier: number,
  density: number
) => {
  const kilograms = milligramsToKilograms(milligrams);
  const metresCubed = kilogramsToMetresCubed(kilograms, density);
  const litres = metresCubedToLitres(metresCubed);
  const volume = litresToVolume(litres, multiplier);

  return volume;
};

export const cupsToGrams = (cups: number, density: number) => {
  const litres = volumeToLitres(cups, LITRES_TO_CUPS);
  const metresCubed = litresToMetresCubed(litres);
  const kilograms = metresCubedToKilograms(metresCubed, density);
  const grams = kilogramsToGrams(kilograms);

  return grams;
};

export const tablespoonsToGrams = (tablespoons: number, density: number) => {
  const litres = volumeToLitres(tablespoons, LITRES_TO_TABLESPOONS);
  const metresCubed = litresToMetresCubed(litres);
  const kilograms = metresCubedToKilograms(metresCubed, density);
  const grams = kilogramsToGrams(kilograms);

  return grams;
};

export const teaspoonsToGrams = (teaspoons: number, density: number) => {
  const litres = volumeToLitres(teaspoons, LITRES_TO_TEASPOONS);
  const metresCubed = litresToMetresCubed(litres);
  const kilograms = metresCubedToKilograms(metresCubed, density);
  const grams = kilogramsToGrams(kilograms);

  return grams;
};

export const BASE_MULTIPLIER = 100;

/*
Functions for dealing with order mutation amounts
*/
export const toAbsoluteAmount = (
  amount: number,
  unit: Unit,
  density: number = 800,
  servings = 1
) => {
  const value = unit.isMass
    ? volumeToMilligrams(amount / servings, unit.amount, density)
    : (amount / servings) * unit.amount;

  return value;
};

export const toRelativeAmount = (
  amount: number,
  unit: Unit | undefined,
  density: number = 800,
  servings = 1
) => {
  if (!unit) return 0;

  const value = unit.isMass
    ? milligramsToVolume(amount * servings, unit.amount, density)
    : (amount * servings) / unit.amount;

  return parseFloat(value.toFixed(2));
};

export const toRelativePrice = (
  price: Price | undefined,
  order: Pick<Order, "amount">,
  ingredient: Ingredient
) => {
  const units = combineUnits(ingredient);

  if (!price || !units.some((unit) => unit.id == price.unit.id)) {
    return 0;
  }

  // The local "base" unit used as a common exchange
  // Playing the role that 1000 plays in a measurement
  // But custom-unit proof
  const LOCAL_BASE_UNIT = getBaseUnit(units);

  if (!LOCAL_BASE_UNIT) {
    return 0;
  }

  const relativeAmount = toRelativeAmount(
    order.amount,
    LOCAL_BASE_UNIT,
    ingredient.density || undefined
  );

  // The isMass logic is same as non-isMass
  // Conversion to non-isMass is already done when getting specific unit
  const properPrice = price.priceAmount / price.unitAmount;
  // Q: What is exchange rate?
  // A: What we need to multiply the base unit (kg/L) by to get to this unit
  // Example: 1000 / 1000 = 1. kg has an exchange rate of 1
  // Example: 1 / 1000 = 0.001. mg has an exchange rate of 0.001
  const exchangeRate = price.unit.amount / LOCAL_BASE_UNIT.amount;

  const value = (properPrice / exchangeRate / BASE_MULTIPLIER) * relativeAmount;
  //            |-------------- relative price --------------|   |-- amount --|
  //            |--------- cents -----------|
  // {properPrice / exchangeRate} gives us cents
  // Dividing by BASE_MULTIPLIER (100) gives us dollars

  return parseFloat(value.toFixed(2));
};

/*
 * Sometimes an ingredients measurement method is changed,
 * leading to a mismatch between the previously set ingredientUnit
 * and the new measurement method's units (or lack thereof)
 *
 * This function aims to resolve this possible mismatch
 */
export const getUnitOrFallback = (units: Unit[], defaultUnit?: Unit | null) =>
  units.some((unit) => unit.id == defaultUnit?.id)
    ? defaultUnit
    : getBaseUnit(units);

export const getBaseLog = (base = 10, y = 10) => Math.log(y) / Math.log(base);

const decimalToFraction = (amount: number | string) => {
  const num = stringIsValidNumber(String(amount))
    ? stringToNumber(String(amount))
    : Number(amount);
  const decimal = num % 1;
  let integer = num - decimal;

  let fraction = `${decimal.toFixed(2).replace("0.", ".")}`;

  const thresholds: [number, string][] = [
    [0, ""],
    [0.125, " 1/8"],
    [0.25, " 1/4"],
    [0.33, " 1/3"],
    [0.375, " 3/8"],
    [0.5, " 1/2"],
    [0.625, " 5/8"],
    [0.66, " 2/3"],
    [0.75, " 3/4"],
    [0.875, " 7/8"],
    [0.9, "ROUND"],
  ];

  const [appropriateThreshold] = thresholds.reduce(
    (prev, [threshold]) => {
      const distance = Math.abs(decimal - threshold);

      if (distance < prev[1]) {
        return [threshold, distance];
      }

      return prev;
    },
    [0, 1]
  );

  fraction =
    thresholds.find(
      ([threshold, label]) => threshold == appropriateThreshold
    )?.[1] || "";

  if (fraction == "ROUND") {
    integer += 1;
    fraction = "";
  }

  return `${integer || ""}${fraction}`;
};

export const formatAmount = (format: string, amount: number | string) =>
  format == "fraction" ? decimalToFraction(amount) : amount;

/*
 * Code to determine the appropriate unit to be used based on boundaries.
 * Where there is no boundary, the boundary is assumed to be 1.
 * Units are check top down. If a unit does not cros it's boundary we move down.
 *
 * For example, if we start at Kilograms and the amount is 0.5 we move down
 * Grams are next, and the value is 500, so we settle for grams.
 */
const boundaries = {
  // Teaspoons (W)
  cjpw6c0im5i1f0a49pquulm6r: 50,
  // Tablespoons (W)
  cjpw6c0ii5i1d0a493vvlsxey: 12,
  // Cups (W)
  cjpw6c0ih5i1b0a49j0id4hvp: 5,
  // Cups
  cjp3dvio9ngnt0a079rgsp88d: 4,
  // Tablespoons
  cjp3dvip2ngnv0a07ri9uqs5v: 250,
  // Teaspoons
  cjp3dvip6ngnx0a079f6norwu: 200,
};

export function determineAppropriateUnit(
  units: Unit[],
  unit: Unit | undefined,
  amount: number,
  density?: number,
  excludingIsMass?: boolean
): Unit {
  const relativeAmountsUnsorted = units
    .map((u) => ({
      ...u,
      relativeAmount: toRelativeAmount(amount, u, density),
    }))
    .filter((u) => (excludingIsMass ? !u.isMass : true));

  const relativeAmounts = sortBy(relativeAmountsUnsorted, "relativeAmount");

  let result = unit || units[0];

  for (let index = 0; index < relativeAmounts.length; index++) {
    const current = relativeAmounts[index];

    // Check if there is a next
    const next = relativeAmounts[index + 1];
    if (next) {
      // Continue if the current relativeAmount is less than the limit
      const limit = boundaries[current.id] || 1;
      // const currentRelativeAmount = toRelativeAmount(amount, current, density);
      if (current.relativeAmount < limit) {
        // continue
      } else {
        result = current;
        break;
      }
    } else {
      result = current;
      break;
    }
  }

  return result;
}

/*
 * Code to determine the appropriate unit to compare a discrete unit to.
 *
 * The unit is determined based on what the relative amount would be.
 * Closest to 1 wins based on logarithmic distance i.e orders of magnitude.
 */
export function findAppropriateComparisonUnit(
  unit: Unit,
  units: Unit[],
  density?: number
): Unit {
  const appropriateUnit = units
    .filter((unit) => (density ? true : !unit.isMass))
    .reduce((acc, cur) => {
      // Relative amount of the defending champion 💪🏾
      const accRelative = toRelativeAmount(unit.amount, acc, density);
      const accLogDistance = Math.abs(getBaseLog(10, accRelative));

      // Relative amount of the challenger
      const curRelative = toRelativeAmount(unit.amount, cur, density);
      const curLogDistance = Math.abs(getBaseLog(10, curRelative));

      // console.log(
      //   "acc",
      //   acc.amount,
      //   accRelative,
      //   getBaseLog(10, accRelative),
      //   accLogDistance
      // );
      // console.log(
      //   "cur",
      //   cur.amount,
      //   curRelative,
      //   getBaseLog(10, curRelative),
      //   curLogDistance
      // );

      // Return the lower logarithmic distance
      if (accLogDistance < curLogDistance) {
        return acc;
      } else {
        return cur;
      }
    });

  return appropriateUnit;
}

//           Regex Fragment
//          ============================================================|======================
//           ^(                                                      )$ | Match every character
//             (         )|(         )|(           )|(              )   | Match either...
//              \d+\.?\d*   \d*\.?\d+                                   | ... a decimal
//                                      \d+(\/\d+)?                     | ... a fraction
//                                                    (\d+ )\d+\/\d+    | ... or a mixed fraction
export const numberStringRegex =
  /^((\d+\.?\d*)|(\d*\.?\d+)|(\d+(\/\d+)?)|((\d+ )\d+\/\d+))$/;

export const stringIsValidNumber = (str: string) => numberStringRegex.test(str);

export function stringToNumber(str: string) {
  if (numberStringRegex.test(str)) {
    let value = Number(str);

    try {
      value = str
        .split(" ")
        .map((part) => Number(eval(part)))
        .reduce((prev, acc) => prev + acc, 0);
    } catch (error) {
      console.error(error);
    }

    return value && value !== Infinity ? Number(value.toFixed(4)) : 0;
  } else {
    return 0;
  }
}
